import { companiesApi } from '@/lib/api';
import { blobToDataUrl } from '@/lib/data-url';
import { useQuery } from '@tanstack/react-query';

/**
 * A stored logo's `src` is a path under the API (`/companies/:id/logo?v=…`);
 * anything else (an old external URL, a `data:` preview) is a full URL the
 * browser can load as it is.
 */
export function isStoredLogoSrc(src: string): boolean {
  return src.startsWith('/');
}

/**
 * The URL an `<img>` can load for a stored logo. An `<img>` cannot carry the
 * session's Authorization header, so the image is fetched with it and shown
 * from a `data:` URL — never a `blob:` one, which an SVG could be opened from as
 * a document of this origin (see `lib/data-url.ts`). The `v` in `src` changes
 * with the image, so each version is fetched once per session and kept: a logo
 * is a few kilobytes, and the data URL is the cache. `enabled` is off for a `src` that needs no fetch.
 */
export function useStoredLogoUrl(src: string): { url: string | undefined; failed: boolean } {
  const query = useQuery({
    queryKey: ['company-logo', src],
    queryFn: async () => blobToDataUrl(await companiesApi.fetchLogo(src)),
    enabled: isStoredLogoSrc(src),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
  return { url: query.data, failed: query.isError };
}

/**
 * What to put in an `<img src>` for a logo's `src`, whichever kind it is;
 * undefined while a stored one is still being fetched. Needs a query client
 * even for a plain URL — callers that may not have one use
 * `CompanyLogoCircle`, which only asks for it when the image is stored.
 */
export function useLogoDisplayUrl(src: string): string | undefined {
  const stored = useStoredLogoUrl(src);
  return isStoredLogoSrc(src) ? stored.url : src;
}
